import { readonly, ref } from "vue";

interface Toast {
  id: string;
  message: string;
  type: "success" | "warning" | "error" | "info";
  duration?: number;
}

interface ToastOptions {
  type?: Toast["type"];
  duration?: number;
}

const toasts = ref<Toast[]>([]);
let toastIdCounter = 0;

export function useToast() {
  function showToast(message: string, options: ToastOptions = {}) {
    const { type = "info", duration = 6000 } = options;
    const id = `toast-${++toastIdCounter}`;

    toasts.value.push({ id, message, type, duration });

    if (duration > 0) {
      setTimeout(() => hideToast(id), duration);
    }

    return id;
  }

  function hideToast(id: string) {
    toasts.value = toasts.value.filter((toast) => toast.id !== id);
  }

  function showSuccess(message: string, options?: Omit<ToastOptions, "type">) {
    return showToast(message, { ...options, type: "success" });
  }

  function showWarning(message: string, options?: Omit<ToastOptions, "type">) {
    return showToast(message, { ...options, type: "warning" });
  }

  function showError(message: string, options?: Omit<ToastOptions, "type">) {
    return showToast(message, { ...options, type: "error" });
  }

  function showInfo(message: string, options?: Omit<ToastOptions, "type">) {
    return showToast(message, { ...options, type: "info" });
  }

  return {
    toasts: readonly(toasts),
    showToast,
    hideToast,
    showSuccess,
    showWarning,
    showError,
    showInfo
  };
}
